// utils/feedbackFormatter.js
import React from 'react';

/**
 * Formats feedback text by converting numbered lists to arrows and adding line breaks
 * @param {string} feedbackText - The raw feedback text
 * @param {string} keyPrefix - Prefix for React keys (default: 'feedback')
 * @returns {Array} Array of React elements and strings
 */
export function formatFeedbackText(rawText, keyPrefix = 'feedback') {
  const text = (rawText ?? '').toString().replace(/\r\n/g, '\n').trim();
  if (!text) return null;

  // Replace long dashed separators with line breaks
  const normalized = text
    .replace(/[\t ]*-{3,}[\t ]*/g, '\n') // sequences of dashes -> newline
    .replace(/\u2014{2,}/g, '\n'); // em-dash sequences -> newline

  // First split by newlines
  const primary = normalized.split('\n');

  // Further split lines that contain embedded numbering like "1.", "2)" etc.
  const splitOnEmbeddedNumbers = (line) =>
    line
      .split(/(?=\b\d+[\.)]\s)/g) // split before occurrences of N. or N)
      .map((s) => s.trim())
      .filter(Boolean);

  // Flattened lines
  let lines = primary.flatMap(splitOnEmbeddedNumbers);

  // Strip leading bullets/numbering and clean
  lines = lines
    .map((l) => l.replace(/^\s*[•*\-–—>]+\s+/g, '')) // remove bullet chars at start
    // Insert space after numbering if missing, e.g. "1)one" -> "1) one", "2.one" -> "2. one"
    .map((l) => l.replace(/^(\d+[\.\)])([^\s])/, '$1 $2'))
    .map((l) => l.replace(/^\s*\d+[\.)]\s+/g, '')) // remove leading numbering like 1. or 2)
    .map((l) => l.replace(/^\s*\.\s*$/g, '')) // drop bare dot
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) return null;

  return (
    <div className="formatted-feedback">
      {lines.map((line, i) => (
        <div className="feedback-line" key={`${keyPrefix}-${i}`}>
          {i === 0
            ? <span className="line-text">&nbsp;{line}</span>
            : <>
                <span className="arrow">➤</span>
                <span className="line-text">{line}</span>
              </>
          }
        </div>
      ))}
    </div>
  );
}